// apps/frontend/src/pages/pipelines/components/ValidationPanel.tsx
import { useState } from 'react';
import { XCircle, AlertTriangle, Info, CheckCircle, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ValidationResult, ValidationIssue } from '@/lib/validation-types';
import { Button } from '@/components/ui/button';

interface ValidationPanelProps {
  validationResult: ValidationResult;
  onJumpToNode?: (nodeId: string) => void;
  onJumpToEdge?: (edgeId: string) => void;
  className?: string;
}

export function ValidationPanel({
  validationResult,
  onJumpToNode,
  onJumpToEdge,
  className,
}: ValidationPanelProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['errors']));

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const handleJumpTo = (issue: ValidationIssue) => {
    if (issue.nodeId && onJumpToNode) {
      onJumpToNode(issue.nodeId);
    } else if (issue.edgeId && onJumpToEdge) {
      onJumpToEdge(issue.edgeId);
    }
  };

  const renderIssueIcon = (severity: string) => {
    if (severity === 'error') {
      return <XCircle className="h-5 w-5 text-red-600" aria-hidden="true" />;
    }
    if (severity === 'warning') {
      return <AlertTriangle className="h-5 w-5 text-yellow-600" aria-hidden="true" />;
    }
    return <Info className="h-5 w-5 text-blue-600" aria-hidden="true" />;
  };

  const renderIssueList = (issues: ValidationIssue[], title: string, sectionKey: string) => {
    if (issues.length === 0) return null;

    const isExpanded = expandedSections.has(sectionKey);

    return (
      <div className="border-b last:border-b-0">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{title}</span>
            <span className="text-xs text-gray-500">({issues.length})</span>
          </div>
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
            aria-hidden="true"
          />
        </button>

        {isExpanded && (
          <div className="px-3 pb-3 space-y-2">
            {issues.map((issue, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                {renderIssueIcon(issue.severity)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{issue.message}</p>
                  {(issue.nodeId || issue.edgeId) && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs text-blue-600 hover:text-blue-800"
                      onClick={() => handleJumpTo(issue)}
                    >
                      Jump to {issue.nodeId ? 'node' : 'edge'} →
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cn('rounded-lg border bg-white overflow-hidden', className)}>
      <div className="p-3 border-b bg-gray-50">
        <h3 className="text-sm font-semibold">Validation Issues</h3>
      </div>

      {validationResult.isValid && validationResult.errors.length === 0 && validationResult.warnings.length === 0 ? (
        <div className="p-4 text-center text-sm text-gray-500">
          <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" aria-hidden="true" />
          <p className="font-medium text-green-600">All checks passed!</p>
          <p className="text-xs mt-1">Your pipeline is ready to execute.</p>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          {renderIssueList(validationResult.errors, 'Errors', 'errors')}
          {renderIssueList(validationResult.warnings, 'Warnings', 'warnings')}
          {renderIssueList(validationResult.suggestions, 'Suggestions', 'suggestions')}
        </div>
      )}
    </div>
  );
}
